import React from 'react';
import { Info, MapPin, Users } from 'lucide-react';

/**
 * Секция основной информации о бюджете
 */
const BudgetInfoSection = ({ 
  formData, 
  handleInputChange, 
  errors,
  russianRegions
}) => {
  return (
    <div className="mb-6">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Info className="w-5 h-5 mr-2 text-emerald-500" />
        Основная информация
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Название бюджета */}
        <div className="col-span-full">
          <label htmlFor="budgetName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Название бюджета
          </label>
          <input
            type="text"
            id="budgetName"
            name="budgetName"
            value={formData.budgetName}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.budgetName ? 'input-error' : ''}`}
            placeholder="Например: Семейный бюджет Ивановых"
          />
          {errors.budgetName && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.budgetName}
            </p>
          )}
        </div>

        {/* Тип бюджета */}
        <div>
          <label htmlFor="budgetType" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Тип бюджета
          </label>
          <select
            id="budgetType"
            name="budgetType"
            value={formData.budgetType}
            onChange={handleInputChange}
            className="form-select w-full"
          >
            <option value="personal">Личный</option>
            <option value="couple">Семейная пара</option>
            <option value="family">Семейный</option>
          </select>
        </div>

        {/* Период расчета */}
        <div>
          <label htmlFor="budgetPeriod" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Период расчета
          </label>
          <select
            id="budgetPeriod"
            name="budgetPeriod"
            value={formData.budgetPeriod}
            onChange={handleInputChange}
            className="form-select w-full"
          >
            <option value="monthly">Ежемесячный</option>
            <option value="yearly">Годовой</option>
          </select>
        </div>

        {/* Количество членов семьи */}
        <div>
          <label htmlFor="familyMembers" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <Users className="w-4 h-4 mr-1 text-emerald-500" />
            Количество взрослых
          </label>
          <input
            type="number"
            id="familyMembers"
            name="familyMembers"
            value={formData.familyMembers}
            onChange={handleInputChange}
            min="1"
            max="10"
            className="form-input w-full"
          />
        </div>

        {/* Регион */}
        <div>
          <label htmlFor="region" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <MapPin className="w-4 h-4 mr-1 text-emerald-500" />
            Регион проживания
          </label>
          <select
            id="region"
            name="region"
            value={formData.region}
            onChange={handleInputChange}
            className="form-select w-full"
          >
            {russianRegions.map((region) => (
              <option key={region.id} value={region.id}>
                {region.name}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Регион учитывается при расчете средних показателей и рекомендаций
          </p>
        </div>

        {/* Наличие детей */}
        <div className="col-span-full flex items-center mt-2">
          <input
            type="checkbox"
            id="hasChildren"
            name="hasChildren"
            checked={formData.hasChildren}
            onChange={handleInputChange}
            className="form-checkbox"
          />
          <label htmlFor="hasChildren" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
            В семье есть дети
          </label>
        </div>

        {/* Количество детей (при включенном флаге) */}
        {formData.hasChildren && (
          <div className="col-span-full ml-6 mt-2">
            <label htmlFor="childrenCount" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Количество детей
            </label>
            <input
              type="number"
              id="childrenCount"
              name="childrenCount"
              value={formData.childrenCount}
              onChange={handleInputChange}
              min="1"
              max="10"
              className="form-input w-full max-w-xs"
            />
          </div>
        )}
      </div>

      {/* Информация о потребительской корзине */}
      <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800">
        <h4 className="text-sm font-medium text-blue-700 dark:text-blue-300 mb-2">
          Информация о прожиточном минимуме в вашем регионе
        </h4>
        <p className="text-xs text-blue-600 dark:text-blue-400">
          {formData.region === 'moscow' && 
            "В Москве прожиточный минимум в 2025 году составляет около 24 290 рублей на душу населения, для трудоспособного населения - 27 741 рубль, для пенсионеров - 17 424 рубля, для детей - 21 111 рублей."
          }
          {formData.region === 'spb' && 
            "В Санкт-Петербурге прожиточный минимум в 2025 году составляет около 17 832 рубля на душу населения, для трудоспособного населения - 19 513 рублей, для пенсионеров - 15 069 рублей, для детей - 17 301 рубль."
          }
          {formData.region === 'city_millionaire' && 
            "В крупных городах-миллионниках прожиточный минимум в 2025 году составляет в среднем около 14 500 рублей на душу населения, для трудоспособного населения - 15 800 рублей, для пенсионеров - 12 000 рублей, для детей - 14 000 рублей."
          }
          {formData.region === 'medium_city' && 
            "В средних городах прожиточный минимум в 2025 году составляет в среднем около 13 500 рублей на душу населения, для трудоспособного населения - 14 600 рублей, для пенсионеров - 11 000 рублей, для детей - 13 200 рублей."
          }
          {formData.region === 'small_city' && 
            "В малых городах прожиточный минимум в 2025 году составляет в среднем около 12 500 рублей на душу населения, для трудоспособного населения - 13 500 рублей, для пенсионеров - 10 500 рублей, для детей - 12 500 рублей."
          }
          {formData.region === 'rural' && 
            "В сельской местности прожиточный минимум в 2025 году составляет в среднем около 11 500 рублей на душу населения, для трудоспособного населения - 12 500 рублей, для пенсионеров - 9 500 рублей, для детей - 11 500 рублей."
          }
        </p>
      </div>
    </div>
  );
};

export default BudgetInfoSection;